/**
 * Custom Cart Drawer Component (Section Version)
 * Handles dynamic offer thresholds, progress bar, cart operations,
 * and special order instructions synced to Shopify cart note.
 */
class CustomCartDrawer extends HTMLElement {
    constructor() {
        super();
        this.classes = {
            active: 'active',
            loading: 'is-loading'
        };
        this.removedVariantIds = new Set(); // Track user-removed gifts
        this.lastUnlockedCount = 0;         // Track milestones for confetti
        this.firstLoad = true;              // Suppress confetti on initial load
    }


    connectedCallback() {
        this.open = this.open.bind(this);
        this.close = this.close.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.handleGlobalSubmit = this.handleGlobalSubmit.bind(this);
        this.changeQty = this.changeQty.bind(this);


        document.addEventListener('custom-cart:open', this.open);
        document.addEventListener('cart:item-added', () => { this.fetchCart(); this.open(); });
        document.addEventListener('cart:updated', () => this.fetchCart());
        document.addEventListener('cart:refresh', () => this.fetchCart());
        document.addEventListener('submit', this.handleGlobalSubmit);

        // Listen for Shopify standard Add To Cart event
        document.addEventListener('shopify:cart:lines-update', (e) => {
            if (e.action === 'add' || (e.detail && e.detail.action === 'add')) {
                this.fetchCart();
                this.open();
            }
        });


        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.init());
        } else {
            this.init();
        }
    }


    disconnectedCallback() {
        document.removeEventListener('custom-cart:open', this.open);
        document.removeEventListener('submit', this.handleGlobalSubmit);
        this.removeEventListener('click', this.handleClick);
    }


    init() {
        this.drawer = this.querySelector('.ccd-drawer');
        this.overlay = this.querySelector('.ccd-overlay');
        this.progressBar = this.querySelector('.ccd-progress-fill');
        this.progressText = this.querySelector('.ccd-progress-text');
        this.itemsContainer = this.querySelector('.ccd-items-wrapper');
        this.milestoneLayer = this.querySelector('.ccd-milestones-layer');


        this.offers = this.getOffers();
        console.log('CCD: Offers loaded:', this.offers);


        if (this.offers.length > 0) {
            this.offers.sort((a, b) => a.threshold - b.threshold);
            this.renderMilestones();
        } else {
            setTimeout(() => {
                this.offers = this.getOffers();
                if (this.offers.length > 0) {
                    this.offers.sort((a, b) => a.threshold - b.threshold);
                    this.renderMilestones();
                    if (this.lastCart) this.updateProgress(this.lastCart);
                }
            }, 500);
        }


        this.removeEventListener('click', this.handleClick);
        this.addEventListener('click', this.handleClick);


        this.fetchCart();
        this.initCoupons();
        this.initSpecialInstructions();
        this.bindFooterEvents();
    }


    // ─────────────────────────────────────────────
    //  SPECIAL INSTRUCTIONS
    // ─────────────────────────────────────────────


    initSpecialInstructions() {
        const toggle = this.querySelector('#ccd-instructions-toggle');
        const body = this.querySelector('#ccd-instructions-body');
        const textarea = this.querySelector('#ccd-note-input');
        const saveBtn = this.querySelector('#ccd-save-note');
        const savedMsg = this.querySelector('#ccd-note-saved');
        const errorMsg = this.querySelector('#ccd-note-error');
        const hiddenInput = this.querySelector('#ccd-note-hidden');


        if (!toggle || !textarea) return;


        // ── Restore saved note ──────────────────────
        // Priority: Shopify cart note (via hidden field set in Liquid) → sessionStorage fallback
        const liquidNote = hiddenInput ? hiddenInput.value.trim() : '';
        const sessionNote = sessionStorage.getItem('ccd_cart_note') || '';
        const restoredNote = liquidNote || sessionNote;


        if (restoredNote) {
            textarea.value = restoredNote;
            if (hiddenInput) hiddenInput.value = restoredNote;
            // Auto-open the panel if a note already exists
            this._openInstructions(toggle, body);
        }


        // ── Toggle open / close ─────────────────────
        toggle.addEventListener('click', () => {
            const isOpen = body.classList.contains('open');
            if (isOpen) {
                this._closeInstructions(toggle, body);
            } else {
                this._openInstructions(toggle, body);
            }
        });


        // ── Live sync to hidden checkout field ──────
        textarea.addEventListener('input', () => {
            if (hiddenInput) hiddenInput.value = textarea.value;
            // Reset feedback messages on new input
            if (savedMsg) savedMsg.style.display = 'none';
            if (errorMsg) errorMsg.style.display = 'none';
        });


        // ── Save button → POST to /cart/update.js ──
        if (saveBtn) {
            saveBtn.addEventListener('click', async () => {
                const note = textarea.value.trim();
                saveBtn.disabled = true;
                saveBtn.textContent = 'Saving...';
                if (savedMsg) savedMsg.style.display = 'none';
                if (errorMsg) errorMsg.style.display = 'none';


                try {
                    const res = await fetch('/cart/update.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ note: note })
                    });


                    if (!res.ok) throw new Error('Cart update failed');


                    // Persist locally so it survives re-renders
                    sessionStorage.setItem('ccd_cart_note', note);
                    if (hiddenInput) hiddenInput.value = note;


                    if (savedMsg) {
                        savedMsg.style.display = 'inline';
                        setTimeout(() => { savedMsg.style.display = 'none'; }, 3000);
                    }
                    console.log('CCD: Cart note saved →', note);


                } catch (e) {
                    console.error('CCD: Note save failed', e);
                    if (errorMsg) {
                        errorMsg.style.display = 'inline';
                        setTimeout(() => { errorMsg.style.display = 'none'; }, 4000);
                    }
                } finally {
                    saveBtn.disabled = false;
                    saveBtn.textContent = 'Save';
                }
            });
        }
    }


    _openInstructions(toggle, body) {
        body.classList.add('open');
        toggle.classList.add('open');
    }


    _closeInstructions(toggle, body) {
        body.classList.remove('open');
        toggle.classList.remove('open');
    }


    // ─────────────────────────────────────────────
    //  FOOTER / CHECKOUT
    // ─────────────────────────────────────────────


    bindFooterEvents() {
        const checkoutForm = this.querySelector('#ccd-cart-form');
        if (checkoutForm) {
            // If a discount code is active, redirect to checkout with it applied
            checkoutForm.addEventListener('submit', (e) => {
                const activeCode = sessionStorage.getItem('ccd_discount_code');
                if (activeCode) {
                    e.preventDefault();
                    // Sync note one last time before navigating
                    const textarea = this.querySelector('#ccd-note-input');
                    const hiddenInput = this.querySelector('#ccd-note-hidden');
                    if (textarea && hiddenInput) hiddenInput.value = textarea.value;


                    // Build URL with discount applied
                    const note = hiddenInput ? hiddenInput.value : '';
                    let url = `/checkout?discount=${encodeURIComponent(activeCode)}`;
                    if (note) url += `&note=${encodeURIComponent(note)}`;
                    window.location.href = url;
                }
            });
        }
    }


    // ─────────────────────────────────────────────
    //  OFFERS / MILESTONES
    // ─────────────────────────────────────────────


    getOffers() {
        try {
            const script = this.querySelector('#ccd-offers-data');
            if (!script) return [];
            return JSON.parse(script.textContent);
        } catch (e) {
            console.error('CCD: Error parsing offers', e);
            return [];
        }
    }


    renderMilestones() {
        if (!this.milestoneLayer || !this.offers.length) return;
        this.milestoneLayer.innerHTML = '';


        const count = this.offers.length;
        const widthPerBlock = 100 / count;


        this.offers.forEach((offer, index) => {
            const percent = (index + 1) * widthPerBlock;
            const el = document.createElement('div');
            el.className = 'ccd-milestone';
            el.style.left = `${percent}%`;
            el.dataset.threshold = offer.threshold;


            const moneyVal = 'Rs. ' + (offer.threshold / 100).toLocaleString();


            let iconHtml = '';
            if (offer.icon && !offer.icon.includes('no-image')) {
                iconHtml = `<img src="${offer.icon}" class="icon-default" style="width:100%; height:100%; object-fit:contain; border-radius:50%;" alt="Reward">`;
            } else {
                iconHtml = `<svg class="icon-default" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path><line x1="7" y1="7" x2="7.01" y2="7"></line></svg>`;
            }


            el.innerHTML = `
                <div class="ccd-milestone-label-top">${offer.rewardText}</div>
                <div class="ccd-milestone-icon">
                   ${iconHtml}
                   <svg class="icon-check" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" style="display:none;"><polyline points="20 6 9 17 4 12"></polyline></svg>
                </div>
                <div class="ccd-milestone-label-bottom">${moneyVal}</div>
            `;
            this.milestoneLayer.appendChild(el);
        });
    }


    // ─────────────────────────────────────────────
    //  EVENT HANDLING
    // ─────────────────────────────────────────────


    handleClick(e) {
        if (e.target.closest('.ccd-close-btn') || e.target.closest('.ccd-overlay')) {
            this.close();
        }


        const removeBtn = e.target.closest('.ccd-remove-btn');
        if (removeBtn) {
            e.preventDefault();
            this.changeQty(removeBtn.dataset.line, 0);
        }
    }


    async handleGlobalSubmit(e) {
        if (e.target.action && e.target.action.includes('/cart/add')) {
            e.preventDefault();
            const submitBtn = e.target.querySelector('[type="submit"]');
            const originalText = submitBtn ? submitBtn.innerText : '';
            if (submitBtn) { submitBtn.setAttribute('disabled', 'true'); submitBtn.innerText = 'Adding...'; }
            const formData = new FormData(e.target);
            try {
                await fetch('/cart/add.js', { method: 'POST', body: formData });
                await this.fetchCart();
                this.open();
            } catch (err) {
                console.error('CCD: Add to cart failed', err);
            } finally {
                if (submitBtn) { submitBtn.removeAttribute('disabled'); submitBtn.innerText = originalText; }
            }
        }
    }


    // ─────────────────────────────────────────────
    //  DRAWER OPEN / CLOSE
    // ─────────────────────────────────────────────


    open() {
        if (this.drawer) {
            this.classList.add(this.classes.active);
            document.body.style.overflow = 'hidden';
        } else {
            console.warn('CCD: Drawer not found, initializing...');
            this.init();
            if (this.querySelector('.ccd-drawer')) {
                this.classList.add(this.classes.active);
                document.body.style.overflow = 'hidden';
            }
        }
    }


    close() {
        this.classList.remove(this.classes.active);
        document.body.style.overflow = '';
    }


    // ─────────────────────────────────────────────
    //  CART FETCH & RE-RENDER
    // ─────────────────────────────────────────────


    async fetchCart() {
        try {
            const res = await fetch('/cart.js');
            const cart = await res.json();
            this.lastCart = cart;


            try {
                const sectionUrl = `/?section_id=${this.dataset.sectionId}`;
                const sectionRes = await fetch(sectionUrl);
                const sectionText = await sectionRes.text();


                const doc = new DOMParser().parseFromString(sectionText, 'text/html');
                const newItems = doc.querySelector('.ccd-items-wrapper');
                if (newItems && this.itemsContainer) {
                    this.itemsContainer.innerHTML = newItems.innerHTML;
                }


                const newFooter = doc.querySelector('.ccd-footer');
                const currentFooter = this.querySelector('.ccd-footer');
                if (newFooter && currentFooter) {
                    currentFooter.innerHTML = newFooter.innerHTML;
                    this.bindFooterEvents();
                }


                // Re-init dynamic features after DOM replacement
                this.initCoupons();
                this.initSpecialInstructions();


            } catch (htmlErr) {
                console.warn('CCD: HTML fetch error', htmlErr);
            }


            this.updateProgress(cart);


            // Keep the theme's header cart icon (count bubble) in sync
            this.refreshHeaderCart();


        } catch (err) {
            console.error('CCD: FetchCart error', err);
        }
    }


    // ─────────────────────────────────────────────
    //  HEADER CART ICON SYNC
    //  Updates the cart count in real-time instantly and
    //  syncs from the server via Section Rendering API.
    // ─────────────────────────────────────────────

    async refreshHeaderCart() {
        try {
            const count = this.lastCart ? (this.lastCart.item_count || 0) : 0;

            // 1. Instant real-time update in DOM for immediate feedback
            const countEls = document.querySelectorAll('.custom-cart-count, [data-cart-count]');
            countEls.forEach(el => {
                el.textContent = count;
            });

            // 2. Section Rendering API for Custom Header (.custom-cart-link)
            const customCartLink = document.querySelector('.custom-cart-link');
            if (customCartLink) {
                const wrapper = customCartLink.closest('[id^="shopify-section-"]');
                if (wrapper) {
                    const sectionId = wrapper.id.replace('shopify-section-', '');
                    const res = await fetch(`${window.location.pathname}?section_id=${sectionId}`);
                    if (res.ok) {
                        const text = await res.text();
                        const doc = new DOMParser().parseFromString(text, 'text/html');
                        const newCartLink = doc.querySelector('.custom-cart-link');
                        if (newCartLink) {
                            customCartLink.innerHTML = newCartLink.innerHTML;
                        }
                    }
                }
            }

            // 3. Fallback for standard theme <cart-icon> if present
            const currentIcon = document.querySelector('cart-icon');
            if (currentIcon) {
                const wrapper = currentIcon.closest('[id^="shopify-section-"]');
                if (wrapper) {
                    const sectionId = wrapper.id.replace('shopify-section-', '');
                    const res = await fetch(`${window.location.pathname}?section_id=${sectionId}`);
                    if (res.ok) {
                        const text = await res.text();
                        const doc = new DOMParser().parseFromString(text, 'text/html');
                        const newIcon = doc.querySelector('cart-icon');
                        if (newIcon) {
                            currentIcon.innerHTML = newIcon.innerHTML;
                            currentIcon.className = newIcon.className;
                        }
                    }
                }
            }
        } catch (e) {
            console.warn('CCD: header cart refresh failed', e);
        }
    }


    // ─────────────────────────────────────────────
    //  QUANTITY CHANGE
    // ─────────────────────────────────────────────


    async changeQty(line, qty) {
        const rows = this.querySelectorAll('.ccd-item');
        if (rows[line - 1]) rows[line - 1].style.opacity = '0.5';


        if (qty === 0 && this.lastCart && this.lastCart.items[line - 1]) {
            const variantId = this.lastCart.items[line - 1].variant_id;
            this.removedVariantIds.add(variantId);
            console.log('CCD: User removed item', variantId);
        }


        try {
            await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ line: line, quantity: qty })
            });
            this.fetchCart();
        } catch (e) {
            console.error('CCD: Qty change error', e);
            if (rows[line - 1]) rows[line - 1].style.opacity = '1';
        }
    }


    // ─────────────────────────────────────────────
    //  PROGRESS BAR & MILESTONES
    // ─────────────────────────────────────────────


    updateProgress(cart) {
        if (!cart) return;
        const totalPrice = cart.total_price;

        // Update header item count
        const title = this.querySelector('.ccd-title');
        if (title) {
            // Also attempt to get the custom title from the server-rendered header if possible, or fallback to 'Your Cart'
            const currentTitleText = title.childNodes[0] ? title.childNodes[0].textContent.trim() : 'Your Cart';
            const baseTitle = currentTitleText.split(' (')[0] || 'Your Cart';
            title.innerHTML = `${baseTitle} (<span id="ccd-count">${cart.item_count || 0}</span> item${cart.item_count === 1 ? '' : 's'})`;
        }

        // Dispatch theme cart update event
        document.dispatchEvent(new CustomEvent('cart:update', {
            detail: {
                resource: cart,
                data: { itemCount: cart.item_count, source: 'custom-cart-drawer' }
            }
        }));
        document.dispatchEvent(new CustomEvent('cart:updated', {
            detail: {
                cart: cart,
                itemCount: cart.item_count
            }
        }));

        if (!this.offers || this.offers.length === 0) return;
        const sortedOffers = this.offers;

        // Piecewise progress calculation
        let percentage = 0;
        const segmentSize = 100 / sortedOffers.length;
        let prevThreshold = 0;

        for (let i = 0; i < sortedOffers.length; i++) {
            const currentThreshold = sortedOffers[i].threshold;

            if (totalPrice < currentThreshold) {
                const range = currentThreshold - prevThreshold;
                const spentInSegment = totalPrice - prevThreshold;
                let segmentProgress = (spentInSegment > 0 ? spentInSegment : 0) / range;
                if (segmentProgress < 0) segmentProgress = 0;
                percentage = (i * segmentSize) + (segmentProgress * segmentSize);
                break;
            } else {
                percentage = (i + 1) * segmentSize;
                prevThreshold = currentThreshold;
            }
        }

        if (totalPrice >= sortedOffers[sortedOffers.length - 1].threshold) percentage = 100;
        if (percentage > 100) percentage = 100;

        // Update fill bar
        if (this.progressBar) {
            this.progressBar.style.width = `${percentage}%`;
        } else {
            this.progressBar = this.querySelector('.ccd-progress-fill');
            if (this.progressBar) this.progressBar.style.width = `${percentage}%`;
        }

        // Update milestone icons & check for confetti
        const milestones = this.querySelectorAll('.ccd-milestone');
        let currentUnlockedCount = 0;


        milestones.forEach(m => {
            const threshold = parseInt(m.dataset.threshold);
            const iconDefault = m.querySelector('.icon-default');
            const iconCheck = m.querySelector('.icon-check');


            if (totalPrice >= threshold) {
                m.classList.add('reached');
                if (iconDefault) iconDefault.style.display = 'none';
                if (iconCheck) iconCheck.style.display = 'block';
                currentUnlockedCount++;
            } else {
                m.classList.remove('reached');
                if (iconDefault) iconDefault.style.display = 'block';
                if (iconCheck) iconCheck.style.display = 'none';
            }
        });


        if (this.firstLoad) {
            this.lastUnlockedCount = currentUnlockedCount;
            this.firstLoad = false;
        } else {
            if (currentUnlockedCount > this.lastUnlockedCount) this.fireConfetti();
            this.lastUnlockedCount = currentUnlockedCount;
        }


        // Status message
        let nextOffer = sortedOffers.find(offer => offer.threshold > totalPrice);
        let message = '';


        if (nextOffer) {
            const amountRemaining = (nextOffer.threshold - totalPrice) / 100;
            const amountText = amountRemaining.toLocaleString('en-IN', { maximumFractionDigits: 2 });
            message = `You are ₹${amountText} away from <strong>${nextOffer.rewardText}</strong>!`;
        } else {
            message = "🎉 You've unlocked all offers!";
        }


        if (this.progressText) this.progressText.innerHTML = message;


        // Auto add/remove free gifts
        this.manageFreeGifts(sortedOffers, cart);
    }


    // ─────────────────────────────────────────────
    //  FREE GIFT MANAGEMENT
    // ─────────────────────────────────────────────


    async manageFreeGifts(allOffers, cart) {
        if (!allOffers.length) return;
        const totalPrice = cart.total_price;
        const unlockedOffers = allOffers.filter(offer => offer.threshold <= totalPrice);
        const lockedOffers = allOffers.filter(offer => offer.threshold > totalPrice);


        let cleanUpNeeded = false;


        // Remove gifts that are no longer qualified
        for (const offer of lockedOffers) {
            if (offer.type !== 'product' || !offer.variantId) continue;


            const lineItem = cart.items.find(item =>
                item.variant_id == offer.variantId &&
                item.properties && (item.properties['_FreeGift'] === 'true' || item.properties['_FreeGift'] === true)
            );


            if (lineItem) {
                console.log('CCD: Removing unqualified gift', offer.rewardText);
                try {
                    const lineIndex = cart.items.indexOf(lineItem) + 1;
                    await fetch('/cart/change.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ line: lineIndex, quantity: 0 })
                    });
                    cleanUpNeeded = true;


                    this.removedVariantIds.delete(parseInt(offer.variantId));
                    this.removedVariantIds.delete(String(offer.variantId));


                } catch (e) { console.error('CCD: Gift removal failed', e); }
            }
        }


        if (cleanUpNeeded) { this.fetchCart(); return; }


        // Add unlocked gifts
        for (const offer of unlockedOffers) {
            if (offer.type === 'product' && offer.variantId) {
                if (this.removedVariantIds.has(parseInt(offer.variantId)) || this.removedVariantIds.has(String(offer.variantId))) {
                    continue;
                }


                const inCart = cart.items.some(item => item.variant_id == offer.variantId);
                if (!inCart) {
                    console.log('CCD: Auto-adding free gift', offer.rewardText);
                    try {
                        await fetch('/cart/add.js', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                id: offer.variantId,
                                quantity: 1,
                                properties: { '_FreeGift': 'true' }
                            })
                        });
                        this.fetchCart();
                        return;
                    } catch (e) { console.error('Auto Add Failed', e); }
                }
            }
        }
    }


    // ─────────────────────────────────────────────
    //  CONFETTI
    // ─────────────────────────────────────────────


    fireConfetti() {
        const container = document.createElement('div');
        container.className = 'ccd-confetti-container';
        container.style.position = 'absolute';
        container.style.top = '0';
        container.style.left = '0';
        container.style.width = '100%';
        container.style.height = '100%';
        container.style.pointerEvents = 'none';
        container.style.zIndex = '999';
        container.style.overflow = 'hidden';


        if (this.drawer) {
            this.drawer.appendChild(container);
        } else {
            this.appendChild(container);
        }


        for (let i = 0; i < 50; i++) {
            const p = document.createElement('div');
            p.className = 'ccd-confetti-piece';
            p.style.left = Math.random() * 100 + '%';
            p.style.animationDelay = (Math.random() * 2) + 's';
            p.style.backgroundColor = `hsl(${Math.random() * 360}, 100%, 50%)`;
            p.style.width = (Math.random() * 8 + 5) + 'px';
            p.style.height = (Math.random() * 8 + 5) + 'px';
            container.appendChild(p);
        }


        setTimeout(() => { container.remove(); }, 5000);
    }


    // ─────────────────────────────────────────────
    //  COUPONS
    // ─────────────────────────────────────────────


    initCoupons() {
        const input = this.querySelector('#ccd-coupon-input');
        const applyBtn = this.querySelector('#ccd-apply-coupon');
        const message = this.querySelector('#ccd-coupon-message');
        const toggleBtn = this.querySelector('#ccd-toggle-coupons');
        const listContainer = this.querySelector('#ccd-coupon-list');
        const countLabel = this.querySelector('#ccd-coupon-count');


        let coupons = [];
        try {
            const script = this.querySelector('#ccd-coupons-data');
            if (script) coupons = JSON.parse(script.textContent);
            console.log('CCD: Coupons loaded:', coupons);
        } catch (e) { console.error('CCD: Coupon Parse Error', e); }


        if (countLabel) countLabel.innerText = `${coupons.length} coupons available`;


        if (listContainer) {
            listContainer.innerHTML = '';
            coupons.forEach(c => {
                const row = document.createElement('div');
                row.style.cssText = 'display:flex; justify-content:space-between; margin-bottom:8px; align-items:center; border-bottom:1px solid #eee; padding-bottom:5px;';
                row.innerHTML = `
                    <div>
                       <div style="font-weight:700; color:#000;">${c.code}</div>
                       <div style="font-size:11px; color:#555;">${c.description}</div>
                    </div>
                    <button class="ccd-use-coupon" data-code="${c.code}" style="background:#000; color:#fff; border:none; border-radius:4px; padding:4px 8px; font-size:10px; cursor:pointer;">APPLY</button>
                `;
                listContainer.appendChild(row);
            });


            if (coupons.length === 0 && toggleBtn) {
                toggleBtn.style.display = 'none';
                if (countLabel) countLabel.innerText = '';
            }
        }


        if (toggleBtn) {
            toggleBtn.addEventListener('click', (e) => {
                e.preventDefault();
                listContainer.style.display = listContainer.style.display === 'none' ? 'block' : 'none';
            });
        }


        this.addEventListener('click', (e) => {
            if (e.target.classList.contains('ccd-use-coupon')) {
                e.preventDefault();
                const code = e.target.dataset.code;
                if (input) input.value = code;
                this.applyCoupon(code);
            }
        });


        if (applyBtn) {
            applyBtn.addEventListener('click', (e) => {
                e.preventDefault();
                const code = input.value.trim();
                if (code) this.applyCoupon(code);
            });
        }


        const activeCode = sessionStorage.getItem('ccd_discount_code');
        if (activeCode && input) {
            input.value = activeCode;
            this.showCouponSuccess(activeCode);
        }
    }


    applyCoupon(code) {
        sessionStorage.setItem('ccd_discount_code', code);
        this.showCouponSuccess(code);
    }


    showCouponSuccess(code) {
        const message = this.querySelector('#ccd-coupon-message');
        const input = this.querySelector('#ccd-coupon-input');
        if (message) {
            message.style.display = 'block';
            message.style.color = '#00b894';
            message.innerHTML = `Discount code <strong>${code}</strong> applied! It will be calculated at checkout.`;
        }
        if (input) {
            input.disabled = true;
            input.style.color = '#00b894';
            input.style.fontWeight = '700';
        }
    }
}


customElements.define('custom-cart-drawer', CustomCartDrawer);

